import { lazy, useEffect } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { MODES, ROLES, permissions } from "@/utils/constants";
import PermissionGate from "@/CommonElements/PermissionGate";
import { MakkahHomeRoute } from "@/Pages/Makkah/MakkahDashboard/MakkahHomeRoute";
import { MadinaHomeRoute } from "@/Pages/MadinaDashboard/MadinaHomeRoute";
import { useSystem } from "@/utils/hooks/useLocation";
import { NoAccess } from "@/CommonElements/NoAccess";
import usePermissions from "@/utils/hooks/usePermissions";
import { UnderConstruction } from "@/Component/UnderConstruction/UnderConstruction";
const lazyNamed = (factory, exportName) =>
  lazy(() => factory().then((module) => ({ default: module[exportName] })));

const CreateUser = lazy(() => import("../Pages/Users/CreateUser"));
const UpdateUser = lazy(() => import("../Pages/Users/UpdateUser"));
const Users = lazy(() => import("../Pages/Users"));
const Groups = lazy(() => import("../Pages/Groups"));
const CreateGroup = lazy(() => import("../Pages/Groups/CreateGroup"));
const UpdateGroup = lazy(() => import("../Pages/Groups/UpdateGroup"));
const SeasonalRecruitment = lazy(() => import("../Pages/SeasonalRecruitment"));
const DisplayUserProfile = lazy(() => import("../Pages/Users/DisplayUserProfile"));
const ResetUserPassword = lazyNamed(() => import("../Pages/ResetUserPassword/ResetUserPassword.jsx"), "ResetUserPassword");
const BasicDataTabs = lazyNamed(() => import("../Pages/BasicData/BasicDataTabs.jsx"), "BasicDataTabs");
const CreateBank = lazyNamed(() => import("../Pages/BasicData/CreateBank.jsx"), "CreateBank");
const EditBank = lazyNamed(() => import("../Pages/BasicData/EditBank.jsx"), "EditBank");
const CreateQualification = lazyNamed(() => import("../Pages/BasicData/CreateQualification.jsx"), "CreateQualification");
const EditQualification = lazyNamed(() => import("../Pages/BasicData/EditQualification.jsx"), "EditQualification");
const CreateDepartment = lazyNamed(() => import("../Pages/BasicData/CreateDepartment.jsx"), "CreateDepartment");
const EditDepartment = lazyNamed(() => import("../Pages/BasicData/EditDepartment.jsx"), "EditDepartment");
const CreateJob = lazyNamed(() => import("../Pages/BasicData/CreateJob.jsx"), "CreateJob");
const EditJob = lazyNamed(() => import("../Pages/BasicData/EditJob.jsx"), "EditJob");
const CreateCity = lazyNamed(() => import("../Pages/BasicData/CreateCity.jsx"), "CreateCity");
const EditCity = lazyNamed(() => import("../Pages/BasicData/EditCity.jsx"), "EditCity");
const CreateInitialGroup = lazyNamed(() => import("../Pages/BasicData/InitialGroup/CreateInitialGroup.jsx"), "CreateInitialGroup");
const UpdateInitialGroup = lazy(() => import("../Pages/BasicData/InitialGroup/UpdateInitialGroup.jsx"));
const CreateInitialCampaign = lazyNamed(() => import("../Pages/BasicData/InitialCampaign/CreateInitialCampaign.jsx"), "CreateInitialCampaign");
const UpdateInitialCampaign = lazy(() => import("../Pages/BasicData/InitialCampaign/UpdateInitialCampaign.jsx"));
const CreatePort = lazyNamed(() => import("../Pages/BasicData/CreatePort.jsx"), "CreatePort");
const EditPort = lazyNamed(() => import("../Pages/BasicData/EditPort.jsx"), "EditPort");
const CreateDirection = lazyNamed(() => import("../Pages/BasicData/CreateDirection.jsx"), "CreateDirection");
const EditDirection = lazyNamed(() => import("../Pages/BasicData/EditDirection.jsx"), "EditDirection");
const CreateServiceCenter = lazyNamed(() => import("../Pages/BasicData/CreateServiceCenter.jsx"), "CreateServiceCenter");
const EditServiceCenter = lazyNamed(() => import("../Pages/BasicData/EditServiceCenter.jsx"), "EditServiceCenter");
const CreateTransportation = lazyNamed(() => import("../Pages/BasicData/CreateTransportation.jsx"), "CreateTransportation");
const EditTransportation = lazyNamed(() => import("../Pages/BasicData/EditTransportation.jsx"), "EditTransportation");
const CreateAirline = lazyNamed(() => import("../Pages/BasicData/CreateAirline.jsx"), "CreateAirline");
const EditAirline = lazyNamed(() => import("../Pages/BasicData/EditAirline.jsx"), "EditAirline");
const CreateState = lazyNamed(() => import("../Pages/BasicData/CreateState.jsx"), "CreateState");
const EditState = lazyNamed(() => import("../Pages/BasicData/EditState.jsx"), "EditState");
const MakkahCampain = lazyNamed(() => import("../Component/MakkahCampain/MakkahCampain.jsx"), "MakkahCampain");
const UpdateCampaign = lazyNamed(() => import("../Pages/Campaigns"), "UpdateCampaign");
const UpdateMakkahGroup = lazy(() => import("../Pages/Makkah/Groups/UpdateGroup"));
const NotFound = lazyNamed(() => import("../Pages/NotFound"), "NotFound");
const PilgrimDetails = lazyNamed(() => import("../Pages/Makkah/PilgrimsAndGroups/PilgrimsDetails"), "PilgrimDetails");

const OrganizersAndContracts = lazyNamed(() => import("../Pages/Makkah/PlanningAndPreparation"), "OrganizersAndContracts");
const Visas = lazyNamed(() => import("../Pages/Makkah/PlanningAndPreparation"), "Visas");
const Housing = lazyNamed(() => import("../Pages/Makkah/PlanningAndPreparation"), "Housing");
const MakkahGroups = lazyNamed(() => import("../Pages/Makkah/PlanningAndPreparation"), "MakkahGroups");
const Camps = lazyNamed(() => import("../Pages/Makkah/PlanningAndPreparation"), "Camps");
const Compliance = lazyNamed(() => import("../Pages/Makkah/PlanningAndPreparation"), "Compliance");

const CenterReceptionJeddah = lazyNamed(() => import("../Pages/Makkah/Reception"), "CenterReceptionJeddah");
const CenterReceptionMadina = lazyNamed(() => import("../Pages/Makkah/Reception"), "CenterReceptionMadina");

const HousingPermits = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations"), "HousingPermits");
const HousingContracts = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations"), "HousingContracts");
const InspectionItems = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations"), "InspectionItems");
const HousingInspection = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations"), "HousingInspection");
const HousingInspectionOrder = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations"), "HousingInspectionOrder");
const BuildingsNotInspected = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations"), "BuildingsNotInspected");

const Pilgrims = lazyNamed(() => import("../Pages/Makkah/PilgrimsAndGroups"), "Pilgrims");
const CampaignAccommodation = lazyNamed(() => import("../Pages/Makkah/PilgrimsAndGroups"), "CampaignAccommodation");
const GroupSettlement = lazyNamed(() => import("../Pages/Makkah/PilgrimsAndGroups"), "GroupSettlement");
const GroupPilgrimsQuery = lazyNamed(() => import("../Pages/Makkah/PilgrimsAndGroups"), "GroupPilgrimsQuery");

const NusukCardsNewBatches = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "NusukCardsNewBatches");
const NusukCardsBatchReturns = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "NusukCardsBatchReturns");
const EditNusukCardsReturn = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "EditNusukCardsReturn");
const NusukCardsReturnDetails = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "NusukCardsReturnDetails");
const EditNusukCardsBatch = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "EditNusukCardsBatch");
const NusukCardsBatchDetails = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "NusukCardsBatchDetails");
const EnterNewReceivedBatchesData = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "EnterNewReceivedBatchesData");
const NusukCardsInquiry = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "NusukCardsInquiry");
const NusukCardsReports = lazyNamed(() => import("../Pages/Makkah/NusukCards"), "NusukCardsReports");

const CampaignSettlementPage = lazy(() => import("../Pages/Makkah/PilgrimsAndGroups/CampaignSettlementPage"));
const Incidents = lazyNamed(() => import("../Pages/Makkah/ReportsAndIncidents"), "Incidents");
const Reports = lazyNamed(() => import("../Pages/Makkah/ReportsAndIncidents"), "Reports");
const CreateReport = lazyNamed(() => import("../Pages/Makkah/ReportsAndIncidents"), "CreateReport");
const ViewReport = lazyNamed(() => import("../Pages/Makkah/ReportsAndIncidents"), "ViewReport");
const InitialDeparturePilgrims = lazyNamed(() => import("../Pages/Makkah/Departure"), "InitialDeparturePilgrims");
const Flights = lazyNamed(() => import("../Pages/Makkah/Departure"), "Flights");
const CreateFlight = lazyNamed(() => import("../Pages/Makkah/Departure"), "CreateFlight");
const PrivateTransportContracts = lazyNamed(() => import("../Pages/Makkah/Departure"), "PrivateTransportContracts");

const CampaignAndGroupReports = lazyNamed(() => import("../Pages/Makkah/ReportsAndStatistics"), "CampaignAndGroupReports");
const ReceptionAndArrivalReports = lazyNamed(() => import("../Pages/Makkah/ReportsAndStatistics"), "ReceptionAndArrivalReports");
const DepartureReports = lazyNamed(() => import("@/Component/shared/reports/departure"), "DepartureReports");
const HousingReports = lazyNamed(() => import("@/Component/shared/reports/housing"), "HousingReports");
const MakkahPilgrimReports = lazyNamed(() => import("../Pages/Makkah/ReportsAndStatistics"), "PilgrimReports");
const IncidentAndReportLogs = lazyNamed(() => import("../Pages/Makkah/ReportsAndStatistics"), "IncidentAndReportLogs");
const ComplianceReports = lazyNamed(() => import("../Pages/Makkah/ReportsAndStatistics"), "ComplianceReports");
const UsersAndServiceCenters = lazyNamed(() => import("../Pages/Makkah/ReportsAndStatistics"), "UsersAndServiceCenters");

const HousingContractDetailReport = lazyNamed(() => import("@/Component/shared/reports/housing"), "HousingContractDetailReport");
const HouseContractCampaignsReport = lazyNamed(() => import("@/Component/shared/reports/housing"), "HouseContractCampaignsReport");
const FoodContractsReport = lazyNamed(() => import("@/Component/shared/reports/housing"), "FoodContractsReport");
const HousingComprehensiveDetailReport = lazyNamed(() => import("@/Component/shared/reports/housing"), "HousingComprehensiveDetailReport");
const HousingBuildingDelegateDetailReport = lazyNamed(() => import("@/Component/shared/reports/housing"), "HousingBuildingDelegateDetailReport");
const HousingInspectionOrdersReport = lazyNamed(() => import("@/Component/shared/reports/housing"), "HousingInspectionOrdersReport");
const DepartureCampaignsReport = lazyNamed(() => import("@/Component/shared/reports/departure"), "DepartureCampaignsReport");
const DepartureGroupsReport = lazyNamed(() => import("@/Component/shared/reports/departure"), "DepartureGroupsReport");
const DepartureRequestReport = lazyNamed(() => import("@/Component/shared/reports/departure"), "DepartureRequestReport");
const PilgrimNationalityStatsReport = lazyNamed(() => import("@/Pages/Makkah/Reports/PilgrimNationalityStatsReport"), "PilgrimNationalityStatsReport");
const PilgrimMissionStatsReport = lazyNamed(() => import("@/Pages/Makkah/Reports/PilgrimMissionStatsReport"), "PilgrimMissionStatsReport");
const AccommodatedPilgrimNationalityStatsReport = lazyNamed(() => import("@/Pages/Makkah/Reports/AccommodatedPilgrimNationalityStatsReport"), "AccommodatedPilgrimNationalityStatsReport");
const AccommodatedPilgrimMissionStatsReport = lazyNamed(() => import("@/Pages/Makkah/Reports/AccommodatedPilgrimMissionStatsReport"), "AccommodatedPilgrimMissionStatsReport");

const ServiceCenters = lazyNamed(() => import("../Pages/Makkah/SystemManagement"), "ServiceCenters");
const SystemSecurity = lazyNamed(() => import("../Pages/Makkah/SystemManagement"), "SystemSecurity");
const Archives = lazyNamed(() => import("../Pages/Makkah/Archives"), "Archives");

const MadinaOrganizersAndContracts = lazyNamed(() => import("../Pages/Madinah/PlanningAndPreparation"), "OrganizersAndContracts");
const MadinaVisas = lazyNamed(() => import("../Pages/Madinah/PlanningAndPreparation"), "Visas");
const MadinaHousing = lazyNamed(() => import("../Pages/Madinah/PlanningAndPreparation"), "Housing");
const MadinaCompliance = lazyNamed(() => import("../Pages/Madinah/PlanningAndPreparation"), "Compliance");

const MadinaHousingInspectionMonitoring = lazyNamed(() => import("../Pages/Madinah/HousingAndAccommodations"), "HousingInspectionMonitoring");
const MadinaFollowUpReadinessReports = lazyNamed(() => import("../Pages/Madinah/HousingAndAccommodations"), "FollowUpReadinessReports");
const MadinaBuildingsNotInspected = lazyNamed(() => import("../Pages/Madinah/HousingAndAccommodations"), "BuildingsNotInspected");
const PilgrimsAndGroupsManagement = lazyNamed(() => import("../Pages/Madinah/PilgrimsAndGroups"), "PilgrimsAndGroupsManagement");
const GroupPilgrimsQueries = lazyNamed(() => import("../Pages/Madinah/PilgrimsAndGroups"), "GroupPilgrimsQueries");
const MadinaIncidents = lazyNamed(() => import("../Pages/Madinah/ReportsAndIncidents"), "Incidents");

const MadinaPilgrimReports = lazyNamed(() => import("../Pages/Madinah/ReportsAndStatistics"), "PilgrimReports");
const MadinaCampaignAndGroupReports = lazyNamed(() => import("../Pages/Madinah/ReportsAndStatistics"), "CampaignAndGroupReports");
const MadinaReceptionAndArrivalReports = lazyNamed(() => import("../Pages/Madinah/ReportsAndStatistics"), "ReceptionAndArrivalReports");
const MadinaDepartureReports = lazyNamed(() => import("../Pages/Madinah/ReportsAndStatistics"), "DepartureReports");
const MadinaHousingReports = lazyNamed(() => import("@/Component/shared/reports/housing"), "HousingReports");
const MadinaIncidentAndReportLogs = lazyNamed(() => import("../Pages/Madinah/ReportsAndStatistics"), "IncidentAndReportLogs");
const MadinaComplianceReports = lazyNamed(() => import("../Pages/Madinah/ReportsAndStatistics"), "ComplianceReports");
const MadinaUsersAndServiceCenters = lazyNamed(() => import("../Pages/Madinah/ReportsAndStatistics"), "UsersAndServiceCenters");

const CampaignGroupPilgrimsReport = lazyNamed(() => import("@/Pages/Madinah/Reports/CampaignGroupPilgrimsReport"), "CampaignGroupPilgrimsReport");
const HousingDetails = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations/HousingDetails.jsx"), "HousingDetails");
const UpdateHousingContract = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations/UpdateHousingContract.jsx"), "UpdateHousingContract");
const ViewHousingContract = lazyNamed(() => import("../Pages/Makkah/HousingAndAccommodations/ViewHousingContract.jsx"), "ViewHousingContract");
const CreateHousingInspection = lazyNamed(() => import("@/Pages/Makkah/HousingAndAccommodations/CreateHousingInspection"), "CreateHousingInspection");
const EditHousingInspection = lazyNamed(() => import("@/Pages/Makkah/HousingAndAccommodations/EditHousingInspection"), "EditHousingInspection");
const HousingInspectionExamination = lazyNamed(() => import("@/Pages/Makkah/HousingAndAccommodations/HousingInspectionExamination"), "HousingInspectionExamination");
const HousingInspectionOrderHouses = lazyNamed(() => import("@/Pages/Makkah/HousingAndAccommodations/HousingInspectionOrderHouses"), "HousingInspectionOrderHouses");
const HousingInspectionNotes = lazy(() => import("@/Pages/HousingInspectionNotes"));
const HouseExaminationItemPage = lazyNamed(() => import("@/Pages/BasicData/CreateHouseExaminationItem"), "HouseExaminationItemPage");
const ExaminationPeriodPage = lazyNamed(() => import("@/Pages/BasicData/CreateExaminationPeriod"), "ExaminationPeriodPage");
const PeriodCodePage = lazyNamed(() => import("@/Pages/BasicData/CreatePeriodCode"), "PeriodCodePage");
const ReceptionJeddah = lazy(() => import("@/Pages/Makkah/Reception/ReceptionJeddah"));
const ReceptionMadina = lazyNamed(() => import("@/Pages/Madinah/Reception"), "ReceptionMadina");
const MakkahBags = lazyNamed(() => import("@/Pages/Madinah/Reception/Bags"), "MakkahBags");
const CustomMadinaBagCreatePage = lazyNamed(
  () => import("@/Pages/Madinah/Reception/Bags"),
  "CustomMadinaBagCreatePage"
);
const CustomBagAccommodatePilgrimsPage = lazyNamed(
  () => import("@/Pages/Madinah/Reception/Bags"),
  "CustomBagAccommodatePilgrimsPage"
);
const DepartureCampaign = lazyNamed(() => import("@/Pages/UpComming/DepartureCampaign"), "DepartureCampaign");
const CampaignAndGroupDeparture = lazyNamed(() => import("@/Pages/CampaignAndGroupDeparture/CampaignAndGroupDeparture"), "CampaignAndGroupDeparture");
const MakkahPilgrimGroupNameMappingFrom = lazyNamed(() => import("@/Pages/BasicData/MakkahPilgrimGroupNameMappingFrom"), "MakkahPilgrimGroupNameMappingFrom");
const JobSectorsForm = lazyNamed(() => import("@/Pages/BasicData/Forms/JobSectorsForm"), "JobSectorsForm");
const ConfirmDepartureList = lazyNamed(() => import("@/Pages/Departure/ConfirmDepartureList"), "ConfirmDepartureList");
const NotComingPilgrimsReport = lazyNamed(() => import("@/Pages/Madinah/Reports/NotComingPilgrimsReport"), "NotComingPilgrimsReport");
const NotAccommodatedPilgrimsReport = lazyNamed(() => import("@/Pages/Madinah/Reports/NotAccommodatedPilgrimsReport"), "NotAccommodatedPilgrimsReport");
const AccommodatedPilgrimsReport = lazyNamed(() => import("@/Pages/Madinah/Reports/AccommodatedPilgrimsReport"), "AccommodatedPilgrimsReport");
const CustomBagPilgrimsPage = lazyNamed(() => import("@/Pages/Madinah/CustomBagPilgrims"), "CustomBagPilgrimsPage");
const DepartureGroupDetail = lazyNamed(() => import("@/Pages/Makkah/PlanningAndPreparation/DepartureGroupDetail"), "DepartureGroupDetail");
const CampaignsAndGroupsReports = lazy(() => import("@/Pages/Makkah/ReportsAndStatistics/CampaignsAndGroupsReports"));
const PilgrimMissionArrivingLeavingStatisticsReport = lazy(() => import("@/Pages/Makkah/ReportsAndStatistics/CampaignsAndGroupsReports/PilgrimMissionArrivingLeavingStatisticsReport"));
const UpcomingGroupsAndCampaignsDetailsReport = lazy(() => import("@/Pages/Makkah/ReportsAndStatistics/CampaignsAndGroupsReports/UpcomingGroupsAndCampaignsDetailsReport"));
const ActivityLogs = lazy(() => import("@/Component/shared/ActivityLogs"));

// `groups-departure/:id/:port/:mode` – permissions are for the **Departure** flow, not `UpcommingGroups`.
// Replace PermissionGate `permission` values with departure-specific full names when product provides them.
const DepartureGroupDetailMode = () => {
  const { mode } = useParams();
  const { currentSystem } = useSystem();
  const isMadina = currentSystem === "madina";
  if (mode === "view") {
    return (
      <PermissionGate
        permissions={[
          isMadina
            ? permissions.view_groups_for_madina
            : permissions.view_groups
        ]}
      >
        <DepartureGroupDetail />
      </PermissionGate>
    );
  }
  if (mode === "edit") {
    return (
      <PermissionGate
        permissions={[
          isMadina
            ? permissions.update_group_details_for_madina
            : permissions.update_group_details
        ]}
      >
        <DepartureGroupDetail />
      </PermissionGate>
    );
  }
  return <NotFound />;
};

const ArrivalCampaignByIdMode = () => {
  const { mode } = useParams();
  return mode === "edit" || mode === "view" ? <UpdateCampaign /> : <NotFound />;
};

const HouseExaminationItemCRUDMode = () => {
  const { mode } = useParams();
  const modes = ["create", "edit", "view"];
  return modes.includes(mode) ? <HouseExaminationItemPage /> : <NotFound />;
};

const ExaminationPeriodCRUDMode = () => {
  const { mode } = useParams();
  const modes = ["create", "edit", "view"];
  return modes.includes(mode) ? <ExaminationPeriodPage /> : <NotFound />;
};

const PeriodCodeCRUDMode = () => {
  const { mode } = useParams();
  const modes = ["create", "edit", "view"];
  return modes.includes(mode) ? <PeriodCodePage /> : <NotFound />;
};

const InitialGroupMode = () => {
  const { mode } = useParams();
  const { currentSystem } = useSystem();
  const isMadina = currentSystem === "madina";
  if (mode === "view") {
    return (
      <PermissionGate
        permissions={[
          isMadina
            ? permissions.view_groups_for_madina
            : permissions.view_groups
        ]}
      >
        <UpdateInitialGroup />
      </PermissionGate>
    );
  }
  if (mode === "edit") {
    return (
      <PermissionGate
        permissions={[
          isMadina
            ? permissions.update_groups_for_madina
            : permissions.update_groups
        ]}
      >
        <UpdateInitialGroup />
      </PermissionGate>
    );
  }
  return <NotFound />;
};

const InitialCampaignMode = () => {
  const { mode } = useParams();
  const { from_id } = useSystem();

  if (mode === "view")
    return (
      <PermissionGate permissions={[from_id === 1 ? permissions.view_campaign_details : permissions.view_campaign_details_for_madina]}>
        <UpdateInitialCampaign />
      </PermissionGate>
    );

  if (mode === "edit")
    return (
      <PermissionGate permissions={[from_id === 1 ? permissions.update_campaigns : permissions.update_campaigns_for_madina]}>
        <UpdateInitialCampaign />
      </PermissionGate>
    );

  return <NotFound />;
};

const ReceptionMode = () => {
  const { port } = useParams();
  const RoutesComponents = {
    JED: <ReceptionJeddah />,
  };

  return RoutesComponents?.[port] || <NotFound />;
};

// const MakkahDashboardMode = () => {
//   return isDevelopment ? <Dashboard /> : ;
// };

const ReceptionMadinaMode = () => {
  const { id, mode } = useParams();

  if (!Object.values(MODES).includes(mode)) return <NotFound />;

  let inner = <ReceptionMadina />;

  if (mode === MODES.create) {
    inner = (
      <PermissionGate permissions={[permissions.create_arrival_reports_for_madina]}>
        {inner}
      </PermissionGate>
    );
  } else if (id && mode === MODES.edit) {
    inner = (
      <PermissionGate permissions={[permissions.update_arrival_reports_for_madina]}>
        {inner}
      </PermissionGate>
    );
  }

  return (
    <PermissionGate permissions={[permissions.list_port_reception_cards_from_nusk_for_madina]}>
      {inner}
    </PermissionGate>
  );
};

const MadinaBagsRoute = () => {
  const { mode } = useParams();
  const isEdit = mode === MODES.edit;
  return (
    <PermissionGate permissions={[permissions.list_arrival_bags_for_madina]}>
      {isEdit ? (
        <PermissionGate permissions={[permissions.update_arrival_bags_for_madina]}>
          <MakkahBags />
        </PermissionGate>
      ) : (
        <MakkahBags />
      )}
    </PermissionGate>
  );
};

const MadinaBagsCreateCustomRoute = () => (
  <PermissionGate permissions={[permissions.list_arrival_bags_for_madina]}>
    <PermissionGate
      mode="any"
      permissions={[permissions.create_arrival_bags_for_madina, permissions.manage_arrival_bags_for_madina]}
    >
      <CustomMadinaBagCreatePage />
    </PermissionGate>
  </PermissionGate>
);

const MadinaBagsAccommodateRoute = () => (
  <PermissionGate permissions={[permissions.accommodate_madina_custom_reception_pilgrims]}>
    <CustomBagAccommodatePilgrimsPage />
  </PermissionGate>
);

const CenterReceptionMode = () => {
  const { port } = useParams();

  const RoutesComponents = {
    JED: (
      <PermissionGate
        permissions={[
          permissions.list_center_reception_cards_from_ports,
          permissions.list_center_reception_cards_between_cities,
        ]}
      >
        <CenterReceptionJeddah />
      </PermissionGate>
    ),
    MED: (
      <PermissionGate
        mode="any"
        permissions={[
          permissions.list_makkah_reception_manifests,
          permissions.list_makkah_reception_manifest_pilgrims,
          permissions.list_makkah_reception_tafweejs,
          permissions.list_makkah_reception_tafweej_pilgrims,
        ]}
      >
        <CenterReceptionMadina />
      </PermissionGate>
    ),
  };

  return RoutesComponents?.[port] || <NotFound />;
};
const GroupMode = () => {
  const { mode } = useParams();
  const { currentSystem } = useSystem();
  const isMadina = currentSystem === "madina";
  if (mode === "view") {
    return (
      <PermissionGate
        permissions={[
          isMadina
            ? permissions.view_groups_for_madina
            : permissions.view_groups
        ]}
      >
        <UpdateMakkahGroup />
      </PermissionGate>
    );
  }
  if (mode === "edit") {
    return (
      <PermissionGate
        permissions={[
          isMadina
            ? permissions.view_groups_for_madina
            : permissions.view_groups
        ]}
      >
        <UpdateMakkahGroup />
      </PermissionGate>
    );
  }
  return <NotFound />;
};

const makkahBasicDataKeys = [
  "cities",
  "ports",
  "directions",
  "service-centers",
  "transportations",
  "airlines",
  "states",
  "house_examination_items",
  "examination_periods",
  "period_codes",
  "makkah-pilgrim-group-name-mappings",
];

const BasicData = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    hasAnyPermission
  } = usePermissions();
  const isSeasonalHr = location.pathname.includes("seasonal_hr");
  const isMadina = location.pathname.includes("madina");
  const basePath = isSeasonalHr
    ? "/app/seasonal_hr/basic-data"
    : isMadina
      ? "/app/madina/basic-data"
      : "/app/makkah/basic-data";
  const keys = isSeasonalHr ? ["qualifications", "banks", "job-sectors", "departments", "jobs"] : makkahBasicDataKeys;
  const defaultKey = isSeasonalHr ? "qualifications" : isMadina ? "cities" : "makkah-pilgrim-group-name-mappings";

  // Redirect from base path to default tab
  useEffect(() => {
    if (!key) {
      navigate(`${basePath}/${defaultKey}`, { replace: true });
    }
  }, [key, basePath, defaultKey, navigate]);

  if (!hasAnyPermission([ROLES.super_admin])) {
    return <NoAccess />;
  }

  if (!keys.includes(key)) {
    return <></>;
  }

  return <BasicDataTabs />;
};

const HousingInspectionOrderFormMode = () => {
  const { mode } = useParams();

  if (mode === "create") return <CreateHousingInspection />;
  return mode === "edit" || mode === "view" ? <EditHousingInspection /> : <NotFound />;
};

const makkahRoutes = [
  {
    path: "makkah",
    Component: <MakkahHomeRoute />,
  },
  {
    path: "makkah/arrival-campaign",
    Component: (
      <PermissionGate mode="any" permissions={[
        permissions.list_campaigns,
        permissions.view_campaigns,
        permissions.create_campaigns,
        permissions.update_campaigns,
        permissions.delete_campaigns,
        permissions.list_campaign_details,
        permissions.view_campaign_details,
        permissions.create_campaign_details,
        permissions.update_campaign_details,
        permissions.delete_campaign_details,
      ]}>
        <MakkahCampain />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/departure-campaign",
    Component: (
      <PermissionGate
        mode="any"
        permissions={[
          permissions.list_departure_campaigns,
          permissions.view_departure_campaigns,
          permissions.create_departure_campaigns,
          permissions.update_departure_campaigns,
          permissions.delete_departure_campaigns,
          permissions.list_departure_campaign_details,
          permissions.view_departure_campaign_details,
          permissions.create_departure_campaign_details,
          permissions.update_departure_campaign_details,
          permissions.delete_departure_campaign_details,
        ]}
      >
        <MakkahCampain />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/departure-campaign/:id/:port/:mode",
    Component: (
      <PermissionGate
        mode="any"
        permissions={[permissions.view_departure_campaigns]}
      >
        <DepartureCampaign />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/campaigns",
    Component: <MakkahCampain />,
  },
  {
    path: "makkah/arrival-campaign/:id/:port/:mode",
    Component: (
      <PermissionGate permissions={[permissions.view_campaigns]}>
        <ArrivalCampaignByIdMode />
      </PermissionGate>
    ),
  },
  // Basic Data Routes - Redirect base path to cities
  {
    path: "makkah/basic-data/makkah-pilgrim-group-name-mappings/:action",
    Component: <MakkahPilgrimGroupNameMappingFrom />,
  },
  {
    path: "makkah/basic-data/makkah-pilgrim-group-name-mappings/:id/:action",
    Component: <MakkahPilgrimGroupNameMappingFrom />,
  },
  {
    path: "makkah/basic-data",
    Component: <BasicData />,
  },
  {
    path: "makkah/basic-data/:key",
    Component: <BasicData />,
  },
  {
    path: "makkah/basic-data/cities/create",
    Component: <CreateCity />,
  },
  {
    path: "makkah/basic-data/cities/:id/edit",
    Component: <EditCity />,
  },
  {
    path: "makkah/basic-data/:key",
    Component: <BasicData />,
  },
  {
    path: "makkah/basic-data/banks/create",
    Component: <CreateBank />,
  },
  {
    path: "makkah/basic-data/banks/:id/edit",
    Component: <EditBank />,
  },
  {
    path: "makkah/basic-data/:key",
    Component: <BasicData />,
  },
  {
    path: "makkah/basic-data/:key",
    Component: <BasicData />,
  },
  {
    path: "makkah/basic-data/job-sectors/create",
    Component: <JobSectorsForm />,
  },
  {
    path: "makkah/basic-data/job-sectors/:id/:action",
    Component: <JobSectorsForm />,
  },
  {
    path: "makkah/basic-data/groups/create",
    Component: (
      <PermissionGate permissions={[permissions.create_groups]}>
        <CreateInitialGroup />
      </PermissionGate>
    ),
  },
  {
    path: "madina/basic-data/groups/create",
    Component: (
      <PermissionGate permissions={[permissions.create_groups_for_madina]}>
        <CreateInitialGroup />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/basic-data/:key",
    Component: <BasicData />,
  },
  {
    path: "makkah/basic-data/house_examination_items/:mode",
    Component: <HouseExaminationItemCRUDMode />,
  },
  {
    path: "makkah/basic-data/house_examination_items/:id/:mode",
    Component: <HouseExaminationItemCRUDMode />,
  },
  {
    path: "makkah/basic-data/examination_periods/:mode",
    Component: <ExaminationPeriodCRUDMode />,
  },
  {
    path: "makkah/basic-data/examination_periods/:id/:mode",
    Component: <ExaminationPeriodCRUDMode />,
  },
  {
    path: "makkah/basic-data/period_codes/:mode",
    Component: <PeriodCodeCRUDMode />,
  },
  {
    path: "makkah/basic-data/period_codes/:id/:mode",
    Component: <PeriodCodeCRUDMode />,
  },
  {
    path: "makkah/basic-data/groups-upcoming",
    Component: <Navigate to="/app/makkah/groups-upcoming" />,
  },
  {
    path: "makkah/basic-data/groups-upcoming/:id/:mode",
    Component: <InitialGroupMode />,
  },
  {
    path: "makkah/basic-data/campaigns/create",
    Component: <CreateInitialCampaign />,
  },
  {
    path: "makkah/basic-data/campaigns/:id/:mode",
    Component: <InitialCampaignMode />,
  },
  {
    path: "makkah/basic-data/ports/create",
    Component: <CreatePort />,
  },
  {
    path: "makkah/basic-data/ports/:id/edit",
    Component: <EditPort />,
  },
  {
    path: "makkah/basic-data/directions/create",
    Component: <CreateDirection />,
  },
  {
    path: "makkah/basic-data/directions/:id/edit",
    Component: <EditDirection />,
  },
  {
    path: "makkah/basic-data/service-centers/create",
    Component: <CreateServiceCenter />,
  },
  {
    path: "makkah/basic-data/service-centers/:id/edit",
    Component: <EditServiceCenter />,
  },
  {
    path: "makkah/basic-data/transportations/create",
    Component: <CreateTransportation />,
  },
  {
    path: "makkah/basic-data/transportations/:id/edit",
    Component: <EditTransportation />,
  },
  {
    path: "makkah/basic-data/airlines/create",
    Component: <CreateAirline />,
  },
  {
    path: "makkah/basic-data/airlines/:id/edit",
    Component: <EditAirline />,
  },
  {
    path: "makkah/basic-data/states/create",
    Component: <CreateState />,
  },
  {
    path: "makkah/basic-data/states/:id/edit",
    Component: <EditState />,
  },
  // Planning and Preparation Routes
  {
    path: "makkah/organizers-and-contracts",
    Component: (
      <PermissionGate permissions={[permissions.view_organizers]}>
        <OrganizersAndContracts />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/visas",
    Component: (
      <PermissionGate permissions={[permissions.view_visas]}>
        <Visas />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing",
    Component: <Housing />,
  },
  {
    path: "makkah/groups-upcoming",
    Component: (
      <PermissionGate permissions={[permissions.list_groups]}>
        <MakkahGroups />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/groups-departure",
    Component: (
      <PermissionGate permissions={[permissions.list_groups]}>
        <MakkahGroups />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/groups-departure/:id/:port/:mode",
    Component: <DepartureGroupDetailMode />,
  },
  {
    path: "makkah/groups-upcoming/:mode",
    Component: <GroupMode />,
  },
  {
    path: "makkah/groups-upcoming/:id/:mode",
    Component: <GroupMode />,
  },
  {
    path: "makkah/camps",
    Component: <Camps />,
  },
  {
    path: "makkah/compliance",
    Component: <Compliance />,
  },
  // Reception Routes
  {
    path: "makkah/:port/reception",
    Component: (
      <PermissionGate
        permissions={[
          permissions.list_port_reception_cards_from_missions,
          permissions.list_port_reception_cards_from_nask,
        ]}
        mode="any"
      >
        <ReceptionMode />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/bags",
    Component: <MadinaBagsRoute />,
  },
  {
    path: "makkah/bags/:bag_id/request/:request_number/:mode",
    Component: <MadinaBagsRoute />,
  },
  // Center Reception Routes
  {
    path: "makkah/:port/center-reception",
    Component: <CenterReceptionMode />,
  },
  // المساكن
  {
    path: "makkah/housing-permits",
    Component: (
      <PermissionGate permissions={[permissions.list_housing]}>
        <HousingPermits />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-permits/:id",
    Component: (
      <PermissionGate permissions={[permissions.view_housing_details]}>
        <HousingDetails />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-permits/:id/edit",
    Component: (
      <PermissionGate permissions={[permissions.edit_housing_data]}>
        <UpdateHousingContract />
      </PermissionGate>
    ),
  },
  // عقود السكن
  {
    path: "makkah/housing-contracts",
    Component: (
      <PermissionGate permissions={[permissions.list_housing_contracts]}>
        <HousingContracts />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-contracts/:id/view",
    Component: (
      <PermissionGate permissions={[permissions.view_housing_contract_details]}>
        <ViewHousingContract />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/inspection-items",
    Component: <InspectionItems />,
  },
  // فحص جاهزية السكن
  {
    path: "makkah/housing-inspection",
    Component: (
      <PermissionGate permissions={[permissions.list_house_inspection_examinations]}>
        <HousingInspection />
      </PermissionGate>
    ),
  },
  // اوامر فحص السكن
  {
    path: "makkah/housing-Inspection-orders",
    Component: (
      <PermissionGate permissions={[permissions.list_house_inspection_orders]}>
        <HousingInspectionOrder />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-Inspection-orders/:order_number/buildings",
    Component: <HousingInspectionOrderHouses />,
  },
  {
    path: "makkah/housing-Inspection-orders/:mode",
    Component: <HousingInspectionOrderFormMode />,
  },
  {
    path: "makkah/housing-Inspection-orders/:order_number/:mode",
    Component: <HousingInspectionOrderFormMode />,
  },
  {
    path: "makkah/housing-Inspection-orders/:order_number/buildings/:house_id/housing-inspection-items",
    Component: <HousingInspectionExamination />,
  },
  // ملاحظات فحص السكن
  {
    path: "makkah/housing-inspection-notes",
    Component: (
      <PermissionGate permissions={[permissions.view_house_inspection_results]}>
        <HousingInspectionNotes />
      </PermissionGate>
    ),
  },
  // بلاغات جاهزية السكن
  {
    path: "makkah/buildings-not-inspected",
    Component: (
      <PermissionGate permissions={[permissions.list_house_inspection_examination_periods]}>
        <BuildingsNotInspected />
      </PermissionGate>
    ),
  },
  // Pilgrims and Groups Routes
  {
    path: "makkah/pilgrims",
    Component: <Pilgrims />,
  },
  {
    path: "makkah/pilgrims/:id/view",
    Component: <PilgrimDetails />,
  },
  {
    path: "makkah/campaign-settlement",
    Component: (
      <PermissionGate permissions={[permissions.view_accommodations]}>
        <CampaignAccommodation />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/campaign-settlement/:type/:id/pilgrims",
    Component: (
      <PermissionGate permissions={[permissions.view_accommodations]}>
        <CampaignSettlementPage />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/group-settlement",
    Component: <GroupSettlement />,
  },
  {
    path: "makkah/group-pilgrims-query",
    Component: (
      <PermissionGate permissions={[permissions.list_makkah_group_pilgrims_inquiry]}>
        <GroupPilgrimsQuery />
      </PermissionGate>
    ),
  },
  // Nusuk Cards Routes
  {
    path: "makkah/nusuk-cards",
    Component: <PermissionGate permissions={[permissions.list_nusuk_cards_batchs]}>
      <NusukCardsNewBatches />
    </PermissionGate>,
  },
  {
    path: "makkah/nusuk-cards/returns",
    Component: <PermissionGate permissions={[permissions.list_nusuk_cards_batch_returns]}>
      <NusukCardsBatchReturns />
    </PermissionGate>,
  },
  {
    path: "makkah/nusuk-cards/returns/:returnId/view",
    Component: <PermissionGate permissions={[permissions.list_nusuk_cards_batch_returns]}>
      <NusukCardsReturnDetails />
    </PermissionGate>,
  },
  {
    path: "makkah/nusuk-cards/returns/:returnId/edit",
    Component: <PermissionGate permissions={[permissions.update_nusuk_cards_batch_returns]}>
      <EditNusukCardsReturn />
    </PermissionGate>,
  },
  {
    path: "makkah/nusuk-cards/enter-new-received-new-batches-data",
    Component: <PermissionGate permissions={[permissions.create_nusuk_cards_batchs]}>
      <EnterNewReceivedBatchesData />
    </PermissionGate>,
  },
  {
    path: "makkah/nusuk-cards/:id/edit",
    Component: <PermissionGate permissions={[permissions.update_nusuk_cards_batchs]}>
      <EditNusukCardsBatch />
    </PermissionGate>,
  },
  {
    path: "makkah/nusuk-cards/:id/view",
    Component: <PermissionGate permissions={[permissions.view_nusuk_cards_batchs]}>
      <NusukCardsBatchDetails />
    </PermissionGate>,
  },
  {
    path: "makkah/nusuk-cards-inquiry",
    Component: <PermissionGate permissions={[permissions.list_nusuk_cards_batchs]}>
      <NusukCardsInquiry />
    </PermissionGate>,
  },
  {
    path: "makkah/nusuk-cards-reports",
    Component: <PermissionGate permissions={[permissions.view_export_nusuk_cards_batch_report]}>
      <NusukCardsReports />
    </PermissionGate>,
  },

  // Reports and Incidents Routes
  {
    path: "makkah/incidents",
    Component: <Incidents />,
  },
  {
    path: "makkah/reports/create",
    Component: (
      <PermissionGate permissions={[permissions.create_makkah_incident_reports]}>
        <CreateReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/reports/:id/view",
    Component: (
      <PermissionGate permissions={[permissions.view_makkah_incident_reports_details]}>
        <ViewReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/reports/:id/edit",
    Component: (
      <PermissionGate permissions={[permissions.update_makkah_incident_reports]}>
        <CreateReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/reports",
    Component: (
      <PermissionGate permissions={[permissions.list_makkah_incident_reports]}>
        <Reports />
      </PermissionGate>
    ),
  },
  // Departure Routes
  {
    path: "makkah/initial-departure-pilgrims",
    Component: (
      <PermissionGate
        permissions={[permissions.list_initial_departure]}
      >
        <InitialDeparturePilgrims />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/initial-departure-pilgrims/:id/pilgrims",
    Component: (
      <PermissionGate
        permissions={[permissions.view_initial_departure]}
      >
        <ConfirmDepartureList />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/departure_campaigns",
    Component: (
      <PermissionGate
        permissions={[permissions.list_departure_campaigns_index]}
      >
        <CampaignAndGroupDeparture />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/departure_groups",
    Component: (
      <PermissionGate
        permissions={[permissions.list_departure_groups_index]}
      >
        <CampaignAndGroupDeparture />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/flights/create",
    Component: <CreateFlight />,
  },
  {
    path: "makkah/flights/edit/:id",
    Component: <CreateFlight />,
  },
  {
    path: "makkah/flights",
    Component: (
      <PermissionGate
        permissions={[permissions.list_flight_details]}
      >
        <Flights />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/private-transport-contracts",
    Component: (
      <PermissionGate
        permissions={[permissions.list_transport_contracts]}
      >
        <PrivateTransportContracts />
      </PermissionGate>
    ),
  },
  // Reports and Statistics Routes
  {
    path: "makkah/pilgrim-reports",
    Component: <MakkahPilgrimReports />,
  },
  {
    path: "makkah/pilgrim-reports/not-coming",
    Component: (
      <PermissionGate permissions={[permissions.view_pilgrim_report_not_coming_makkah]}>
        <NotComingPilgrimsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/pilgrim-reports/not-accommodated",
    Component: (
      <PermissionGate permissions={[permissions.view_pilgrim_report_not_accommodated_makkah]}>
        <NotAccommodatedPilgrimsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/pilgrim-reports/accommodated",
    Component: (
      <PermissionGate permissions={[permissions.view_pilgrim_report_accommodated_madina]}>
        <AccommodatedPilgrimsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/pilgrim-reports/campaign-group-pilgrims",
    Component: (
      <PermissionGate permissions={[permissions.makkah_view_export_campaign_group_pilgrims]}>
        <CampaignGroupPilgrimsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/pilgrim-reports/pilgrim-nationality-stats",
    Component: (
      <PermissionGate permissions={[permissions.view_pilgrim_report_nationality_stats_makkah]}>
        <PilgrimNationalityStatsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/pilgrim-reports/pilgrim-mission-stats",
    Component: (
      <PermissionGate permissions={[permissions.view_pilgrim_report_mission_stats_makkah]}>
        <PilgrimMissionStatsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/pilgrim-reports/accommodated-nationality-stats",
    Component: (
      <PermissionGate permissions={[permissions.view_pilgrim_report_nationality_stats_makkah]}>
        <AccommodatedPilgrimNationalityStatsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/pilgrim-reports/accommodated-mission-stats",
    Component: (
      <PermissionGate permissions={[permissions.view_pilgrim_report_mission_stats_makkah]}>
        <AccommodatedPilgrimMissionStatsReport />
      </PermissionGate>
    ),
  },
  // تقارير الحملات والمجموعات
  {
    path: "makkah/campaigns-and-groups-reports",
    Component: <CampaignsAndGroupsReports />,
  },
  {
    path: "makkah/campaigns-and-groups-reports/detailed-upcoming-groups-ands-campaigns-report",
    Component: (
      <PermissionGate
        permissions={[
          permissions.view_export_makkah_arrival_campaign_segments_report,
          permissions.view_export_makkah_group_details_report,
        ]}
      >
        <CampaignAndGroupReports />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/campaigns-and-groups-reports/pilgrim-mission-arriving-leaving-statistics-report",
    Component: (
      <PermissionGate
        permissions={[permissions.view_export_makkah_housed_arrivals_service_center_distribution_report]}
      >
        <PilgrimMissionArrivingLeavingStatisticsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/campaigns-and-groups-reports/campaigns-and-groups-statement-report",
    Component: (
      <PermissionGate
        permissions={[
          permissions.view_export_makkah_campaign_statement_report,
          permissions.view_export_makkah_group_statement_report,
        ]}
      >
        <UpcomingGroupsAndCampaignsDetailsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/reception-and-arrival-reports",
    Component: <ReceptionAndArrivalReports />,
  },
  {
    path: "makkah/departure-reports",
    Component: 
    <PermissionGate
        permissions={[
          permissions.view_export_departure_request_report,
          permissions.view_export_departure_campaign_report,
        ]}
      >
       <DepartureReports />
      </PermissionGate>,
  },
  {
    path: "makkah/departure-reports/departure-request",
    Component:  
      <PermissionGate permissions={[permissions.view_export_departure_request_report]}>
        <DepartureRequestReport />
      </PermissionGate>,
  },
  {
    path: "makkah/departure-reports/departure-campaigns",
    Component: 
      <PermissionGate permissions={[permissions.view_export_departure_campaign_report]}>
        <DepartureCampaignsReport />
      </PermissionGate>,
  },
  {
    path: "makkah/departure-reports/departure-groups",
    Component: 
      <PermissionGate permissions={[permissions.view_export_departure_group_report]}>
        <DepartureGroupsReport />
      </PermissionGate>,
  },
  {
    path: "makkah/housing-reports",
    Component: <HousingReports />,
  },
  {
    path: "makkah/housing-reports/housing-contract-detail",
    Component: (
      <PermissionGate permissions={[permissions.view_export_housing_contract_detail_report]}>
        <HousingContractDetailReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-reports/house-contract-campaigns",
    Component: (
      <PermissionGate permissions={[permissions.view_export_house_contract_campaigns_report]}>
        <HouseContractCampaignsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-reports/food-contracts",
    Component: (
      <PermissionGate permissions={[permissions.view_export_food_contracts_report]}>
        <FoodContractsReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-reports/housing-comprehensive-detail",
    Component: (
      <PermissionGate permissions={[permissions.view_export_housing_comprehensive_detail_report]}>
        <HousingComprehensiveDetailReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-reports/housing-building-delegate-detail",
    Component: (
      <PermissionGate permissions={[permissions.view_export_housing_building_delegate_detail_report]}>
        <HousingBuildingDelegateDetailReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/housing-reports/housing-inspection-orders-reports",
    Component: (
      <PermissionGate permissions={[permissions.view_export_makkah_house_examination_report]}>
        <HousingInspectionOrdersReport />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/incident-and-report-logs",
    Component: (
      <PermissionGate permissions={[permissions.makkah_view_export_incident_reports]}>
        <IncidentAndReportLogs />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/compliance-reports",
    Component: (
      <PermissionGate permissions={[permissions.makkah_view_export_incident_reports]}>
        <ComplianceReports />
      </PermissionGate>
    ),
  },
  {
    path: "makkah/users-and-service-centers",
    Component: <UsersAndServiceCenters />,
  },
  // System Management Routes
  {
    path: "makkah/service-centers",
    Component: <ServiceCenters />,
  },
  {
    path: "makkah/system-security",
    Component: <ActivityLogs />,
  },
  // Archives Routes
  {
    path: "makkah/archives",
    Component: <Archives />,
  },
];

const usersManagementRoutes = [

  // Users Routes
  {
    path: "user_management/users",
    Component: (
      <PermissionGate permissions={[permissions.list_users_for_user_management]}>
        <Users />
      </PermissionGate>
    ),
  },
  {
    path: "user_management/users/create",
    Component: (
      <PermissionGate permissions={[permissions.create_user_for_user_management]}>
        <CreateUser />
      </PermissionGate>
    ),
  },
  {
    path: "user_management/users/:id/edit",
    Component: (
      <PermissionGate permissions={[permissions.update_user_for_user_management]}>
        <UpdateUser />
      </PermissionGate>
    ),
  },
  {
    path: "user_management/users/:id/reset-password",
    Component: (
      <PermissionGate permissions={[permissions.reset_user_password_for_user_management]}>
        <ResetUserPassword />
      </PermissionGate>
    ),
  },
  {
    path: "user_management/users/:id/profile",
    Component: <DisplayUserProfile />,
  },
  // Groups Routes
  {
    path: "user_management/groups",
    Component: (
      <PermissionGate permissions={[permissions.list_groups_for_user_management]}>
        <Groups />
      </PermissionGate>
    ),
  },
  {
    path: "user_management/groups/create",
    Component: (
      <PermissionGate permissions={[permissions.create_group_for_user_management]}>
        <CreateGroup />
      </PermissionGate>
    ),
  },
  {
    path: "user_management/groups/:id/edit",
    Component: (
      <PermissionGate permissions={[permissions.update_group_for_user_management]}>
        <UpdateGroup />
      </PermissionGate>
    ),
  },
];

const seasonalRecruitmentRoutes = [
  {
    path: "seasonal_hr",
    Component: <SeasonalRecruitment />,
  },
  {
    path: "seasonal_hr/basic-data",
    Component: (
      <PermissionGate permissions={[permissions.basic_data_access]}>
        <BasicData />
      </PermissionGate>
    ),
  },
  {
    path: "seasonal_hr/basic-data/qualifications/create",
    Component: <CreateQualification />,
  },
  {
    path: "seasonal_hr/basic-data/qualifications/:id/edit",
    Component: <EditQualification />,
  },
  {
    path: "seasonal_hr/basic-data/banks/create",
    Component: <CreateBank />,
  },
  {
    path: "seasonal_hr/basic-data/banks/:id/edit",
    Component: <EditBank />,
  },
  {
    path: "seasonal_hr/basic-data/:key",
    Component: <BasicData />,
  },
  {
    path: "seasonal_hr/basic-data/departments/create",
    Component: <CreateDepartment />,
  },
  {
    path: "seasonal_hr/basic-data/departments/:id/edit",
    Component: <EditDepartment />,
  },
  {
    path: "seasonal_hr/basic-data/jobs/create",
    Component: <CreateJob />,
  },
  {
    path: "seasonal_hr/basic-data/jobs/:id/edit",
    Component: <EditJob />,
  },
  {
    path: "seasonal_hr/basic-data/job-sectors/create",
    Component: <JobSectorsForm />,
  },
  {
    path: "seasonal_hr/basic-data/job-sectors/:id/:action",
    Component: <JobSectorsForm />,
  },
];

const madinaRoutes = [
  {
    path: "madina",
    Component: <MadinaHomeRoute />,
  },
  {
    path: "madina/dashboard",
    Component: <MadinaHomeRoute />,
  },
  // Planning and Preparation Routes
  {
    path: "madina/organizers-and-contracts",
    Component: <MadinaOrganizersAndContracts />,
  },
  {
    path: "madina/visas",
    Component: <MadinaVisas />,
  },
  {
    path: "madina/housing",
    Component: <MadinaHousing />,
  },
  // Pilgrims and Groups Routes
  {
    path: "madina/pilgrims",
    Component: <Pilgrims />,
  },
  {
    path: "madina/pilgrims/:id/view",
    Component: <PilgrimDetails />,
  },
  {
    path: "madina/custom-bag-pilgrims",
    Component: (
      <PermissionGate
        mode="any"
        permissions={[
          permissions.list_arrival_bags_for_madina,
          permissions.manage_arrival_bags_for_madina,
        ]}
      >
        <CustomBagPilgrimsPage />
      </PermissionGate>
    ),
  },
  // Nusuk Cards Routes
  {
    path: "madina/nusuk-cards",
    Component: (
      <PermissionGate permissions={[permissions.list_nusuk_cards_batchs_madina]}>
        <NusukCardsNewBatches />
      </PermissionGate>
    ),
  },
  {
    path: "madina/nusuk-cards/returns",
    Component: (
      <PermissionGate permissions={[permissions.list_nusuk_cards_batch_returns_madina]}>
        <NusukCardsBatchReturns />
      </PermissionGate>
    ),
  },
  {
    path: "madina/nusuk-cards/returns/:returnId/view",
    Component: (
      <PermissionGate permissions={[permissions.list_nusuk_cards_batch_returns_madina]}>
        <NusukCardsReturnDetails />
      </PermissionGate>
    ),
  },
  {
    path: "madina/nusuk-cards/returns/:returnId/edit",
    Component: (
      <PermissionGate permissions={[permissions.update_nusuk_cards_batch_returns_madina]}>
        <EditNusukCardsReturn />
      </PermissionGate>
    ),
  },
  {
    path: "madina/nusuk-cards/enter-new-received-new-batches-data",
    Component: (
      <PermissionGate permissions={[permissions.create_nusuk_cards_batchs_madina]}>
        <EnterNewReceivedBatchesData />
      </PermissionGate>
    ),
  },
  {
    path: "madina/nusuk-cards/:id/edit",
    Component: (
      <PermissionGate permissions={[permissions.update_nusuk_cards_batchs_madina]}>
        <EditNusukCardsBatch />
      </PermissionGate>
    ),
  },
  {
    path: "madina/nusuk-cards/:id/view",
    Component: (
      <PermissionGate permissions={[permissions.view_nusuk_cards_batchs_madina]}>
        <NusukCardsBatchDetails />
      </PermissionGate>
    ),
  },
  {
    path: "madina/nusuk-cards-inquiry",
    Component: (
      <PermissionGate permissions={[permissions.list_nusuk_cards_batchs_madina]}>
        <NusukCardsInquiry />
      </PermissionGate>
    ),
  },
  {
    path: "madina/nusuk-cards-reports",
    Component: (
      <PermissionGate permissions={[permissions.view_export_nusuk_cards_batch_report_madina]}>
        <NusukCardsReports />
      </PermissionGate>
    ),
  },

  {
    path: "madina/departure-campaign",
    Component: (
      <PermissionGate
        mode="any"
        permissions={[
          permissions.list_departure_campaigns_for_madina,
          permissions.view_departure_campaigns_for_madina,
          permissions.list_departure_campaign_details_for_madina,
          permissions.view_departure_campaign_details_for_madina,
          permissions.create_departure_campaign_details_for_madina,
          permissions.update_departure_campaign_details_for_madina,
          permissions.delete_departure_campaign_details_for_madina,
        ]}
      >
        <MakkahCampain />
      </PermissionGate>
    ),
  },
  {
    path: "madina/departure-campaign/:id/:port/:mode",
    Component: (
      <PermissionGate
        mode="any"
        permissions={[
          permissions.view_departure_campaigns_for_madina,
          permissions.view_departure_campaign_details_for_madina,
          permissions.view_departure_campaigns_details_for_madina,
          permissions.create_departure_campaign_details_for_madina,
          permissions.update_departure_campaign_details_for_madina,
          permissions.delete_departure_campaign_details_for_madina,
        ]}
      >
        <DepartureCampaign />
      </PermissionGate>
    ),
  },
  {
    path: "madina/center-reception",
    Component: (
      <PermissionGate
        mode="any"
        permissions={[
          permissions.list_madina_reception_manifests,
          permissions.list_madina_reception_manifest_pilgrims,
          permissions.list_madina_reception_tafweejs,
          permissions.list_madina_reception_tafweej_pilgrims,
        ]}
      >
        <CenterReceptionMadina />
      </PermissionGate>
    ),
  },
  {
    path: "madina/arrival-campaign",
    Component: (
      <PermissionGate
        mode="any"
        permissions={[
          permissions.list_campaigns_for_madina,
          permissions.view_campaigns_for_madina,
          permissions.create_campaigns_for_madina,
          permissions.update_campaigns_for_madina,
          permissions.delete_campaigns_for_madina,
          permissions.list_campaign_details_for_madina,
          permissions.view_campaign_details_for_madina,
          permissions.create_campaign_details_for_madina,
          permissions.update_campaign_details_for_madina,
          permissions.delete_campaign_details_for_madina,
        ]}
      >
        <MakkahCampain />
      </PermissionGate>
    ),
  },
  {
    path: "madina/arrival-campaign/:id/:port/:mode",
    Component: (
      <PermissionGate
        mode="any"
        permissions={[
          permissions.view_campaigns_for_madina,
          permissions.view_campaign_details_for_madina,
          permissions.update_campaigns_for_madina,
          permissions.create_campaign_details_for_madina,
          permissions.update_campaign_details_for_madina,
          permissions.delete_campaign_details_for_madina,
        ]}
      >
        <ArrivalCampaignByIdMode />
      </PermissionGate>
    ),
  },
  {
    path: "madina/groups-upcoming",
    Component: (
      <PermissionGate permissions={[permissions.list_groups_for_madina]}>
        <MakkahGroups />
      </PermissionGate>
    ),
  },
  {
    path: "madina/groups-departure",
    Component: (
      <PermissionGate permissions={[permissions.list_groups_for_madina]}>
        <MakkahGroups />
      </PermissionGate>
    ),
  },
  {
    path: "madina/groups-departure/:id/:port/:mode",
    Component: <DepartureGroupDetailMode />,
  },
  {
    path: "madina/groups-upcoming/:mode",
    Component: <GroupMode />,
  },
  {
    path: "madina/groups-upcoming/:id/:mode",
    Component: <GroupMode />,
  },
  {
    path: "madina/compliance",
    Component: <MadinaCompliance />,
  },
  {
    path: "madina/reception",
    Component: (
      <PermissionGate permissions={[permissions.list_port_reception_cards_from_nusk_for_madina]}>
        <ReceptionMadina />
      </PermissionGate>
    ),
  },
  {
    path: "madina/reception/:mode",
    Component: <ReceptionMadinaMode />,
  },
  {
    path: "madina/reception/:id/:mode",
    exact: true,
    Component: <ReceptionMadinaMode />,
  },
  // Bags Routes
  {
    path: "madina/bags",
    Component: <MadinaBagsRoute />,
  },
  {
    path: "madina/bags/custom/create",
    Component: <MadinaBagsCreateCustomRoute />,
  },
  {
    path: "madina/bags/:bag_id/accommodate",
    Component: <MadinaBagsAccommodateRoute />,
  },
  {
    path: "madina/bags/request/:bag_id/:mode",
    Component: <MadinaBagsRoute />,
  },
  // المســاكـن
  {
    path: "madina/housing-permits",
    Component: (
      <PermissionGate permissions={[permissions.list_housing_for_madina]}>
        <HousingPermits />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-permits/:id",
    Component: (
      <PermissionGate permissions={[permissions.view_housing_details_for_madina]}>
        <HousingDetails />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-permits/:id/edit",
    Component: (
      <PermissionGate permissions={[permissions.edit_housing_data_for_madina]}>
        <UpdateHousingContract />
      </PermissionGate>
    ),
  },
  // عقــود السكن
  {
    path: "madina/housing-contracts",
    Component: (
      <PermissionGate permissions={[permissions.list_housing_contracts_for_madina]}>
        <HousingContracts />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-contracts/:id/view",
    Component: (
      <PermissionGate permissions={[permissions.view_housing_contract_details_for_madina]}>
        <ViewHousingContract />
      </PermissionGate>
    ),
  },
  // فحص جاهزية السكن
  {
    path: "madina/inspection-items",
    Component: (
      // <PermissionGate permissions={[permissions.list_house_inspection_examinations_for_madina]}>
      <HousingInspection />
    ),
    // </PermissionGate>,
  },
  {
    path: "madina/housing-inspection",
    Component: (
      <PermissionGate permissions={[permissions.list_house_inspection_examinations_for_madina]}>
        <HousingInspection />
      </PermissionGate>
    ),
  },
  // اوامر فحص السكن
  {
    path: "madina/housing-Inspection-orders",
    Component: (
      <PermissionGate permissions={[permissions.list_house_inspection_orders_for_madina]}>
        <HousingInspectionOrder />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-Inspection-orders/:order_number/buildings",
    Component: <HousingInspectionOrderHouses />,
  },
  {
    path: "madina/housing-Inspection-orders/:order_number/buildings/:house_id/housing-inspection-items",
    Component: <HousingInspectionExamination />,
  },
  // ملاحظات فحص السكن
  {
    path: "madina/housing-inspection-notes",
    Component: (
      <PermissionGate permissions={[permissions.list_house_inspection_track_cards_for_madina]}>
        <HousingInspectionNotes />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-Inspection-orders/:mode",
    Component: <HousingInspectionOrderFormMode />,
  },
  {
    path: "madina/housing-Inspection-orders/:order_number/:mode",
    Component: <HousingInspectionOrderFormMode />,
  },
  {
    path: "madina/housing-inspection-monitoring",
    Component: <MadinaHousingInspectionMonitoring />,
  },
  {
    path: "madina/follow-up-readiness-reports",
    Component: <MadinaFollowUpReadinessReports />,
  },
  {
    path: "madina/buildings-not-inspected",
    Component: <MadinaBuildingsNotInspected />,
  },
  // Pilgrims and Groups Routes
  {
    path: "madina/pilgrims-and-groups-management",
    Component: <PilgrimsAndGroupsManagement />,
  },
  {
    path: "madina/group-pilgrims-queries",
    Component: <GroupPilgrimsQueries />,
  },
  // Reports and Incidents Routes
  {
    path: "madina/incidents",
    Component: <MadinaIncidents />,
  },
  // Madina MASTER_DATA (basic-data) with tabs like Makkah (cities, service-centers, etc.)
  {
    path: "madina/basic-data",
    Component: <BasicData />,
  },
  {
    path: "madina/basic-data/:key",
    Component: <BasicData />,
  },
  {
    path: "madina/basic-data/examination_periods/:mode",
    Component: <ExaminationPeriodCRUDMode />,
  },
  {
    path: "madina/basic-data/examination_periods/:id/:mode",
    Component: <ExaminationPeriodCRUDMode />,
  },
  {
    path: "madina/basic-data/period_codes/:mode",
    Component: <PeriodCodeCRUDMode />,
  },
  {
    path: "madina/basic-data/period_codes/:id/:mode",
    Component: <PeriodCodeCRUDMode />,
  },
  {
    path: "madina/basic-data/service-centers/create",
    Component: <CreateServiceCenter />,
  },
  {
    path: "madina/basic-data/service-centers/:id/edit",
    Component: <EditServiceCenter />,
  },
  {
    path: "madina/basic-data/groups-upcoming",
    Component: <Navigate to="/app/madina/groups-upcoming" />,
  },
  {
    path: "madina/basic-data/groups-upcoming/:id/:mode",
    Component: <InitialGroupMode />,
  },
  {
    path: "madina/basic-data/campaigns/create",
    Component: <CreateInitialCampaign />,
  },
  {
    path: "madina/basic-data/campaigns/:id/:mode",
    Component: <InitialCampaignMode />,
  },
  {
    path: "madina/basic-data/directions/create",
    Component: <CreateDirection />,
  },
  {
    path: "madina/basic-data/directions/:id/edit",
    Component: <EditDirection />,
  },
  {
    path: "madina/reports",
    Component: (
      <PermissionGate permissions={[permissions.list_madina_incident_reports]}>
        <Reports />
      </PermissionGate>
    ),
  },
  {
    path: "madina/reports/:id/view",
    Component: (
      <PermissionGate permissions={[permissions.view_madina_incident_reports_details]}>
        <ViewReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/reports/:action",
    Component: (
      <PermissionGate permissions={[permissions.create_madina_incident_reports]}>
        <CreateReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/reports/:id/:action",
    Component: (
      <PermissionGate permissions={[permissions.update_madina_incident_reports]}>
        <CreateReport />
      </PermissionGate>
    ),
  },
  // Departure Routes
  {
    path: "madina/initial-departure-pilgrims",
    Component: (
      <PermissionGate permissions={[permissions.list_initial_departure_for_madina]}>
        <InitialDeparturePilgrims />
      </PermissionGate>
    ),
  },
  {
    path: "madina/initial-departure-pilgrims/:id/pilgrims",
    Component: (
      <PermissionGate permissions={[permissions.view_initial_departure_for_madina]}>
        <ConfirmDepartureList />
      </PermissionGate>
    ),
  },
  {
    path: "madina/departure_campaigns",
    Component: (
      <PermissionGate permissions={[permissions.list_departure_campaigns_index_for_madina]}>
        <CampaignAndGroupDeparture />
      </PermissionGate>
    ),
  },
  {
    path: "madina/departure_groups",
    Component: (
      <PermissionGate permissions={[permissions.list_departure_groups_index_for_madina]}>
        <CampaignAndGroupDeparture />
      </PermissionGate>
    ),
  },
  {
    path: "madina/flights/create",
    Component: <CreateFlight />,
  },
  {
    path: "madina/flights/edit/:id",
    Component: <CreateFlight />,
  },
  {
    path: "madina/flights",
    Component: (
      <PermissionGate permissions={[permissions.list_flight_details_for_madina]}>
        <Flights />
      </PermissionGate>
    ),
  },
  {
    path: "madina/private-transport-contracts",
    Component: (
      <PermissionGate permissions={[permissions.list_transport_contracts_for_madina]}>
        <PrivateTransportContracts />
      </PermissionGate>
    ),
  },
  // Reports and Statistics Routes
  {
    path: "madina/pilgrim-reports",
    Component: <MadinaPilgrimReports />,
  },
  {
    path: "madina/pilgrim-reports/not-coming",
    Component: <NotComingPilgrimsReport />,
  },
  {
    path: "madina/pilgrim-reports/not-accommodated",
    Component: <NotAccommodatedPilgrimsReport />,
  },
  {
    path: "madina/pilgrim-reports/accommodated",
    Component: (
      <PermissionGate permissions={[permissions.view_pilgrim_report_accommodated_madina]}>
        <AccommodatedPilgrimsReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/pilgrim-reports/campaign-group-pilgrims",
    Component: (
      <PermissionGate permissions={[permissions.madina_view_export_campaign_group_pilgrims]}>
        <CampaignGroupPilgrimsReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/pilgrim-reports/accommodated-nationality-stats",
    Component: <AccommodatedPilgrimNationalityStatsReport />,
  },
  {
    path: "madina/pilgrim-reports/accommodated-mission-stats",
    Component: <AccommodatedPilgrimMissionStatsReport />,
  },
  // تقارير الحملات والمجموعات
  {
    path: "madina/campaigns-and-groups-reports",
    Component: <MadinaCampaignAndGroupReports />,
  },
  {
    path: "madina/campaigns-and-groups-reports/detailed-upcoming-groups-ands-campaigns-report",
    Component: (
      <PermissionGate
        permissions={[
          permissions.view_export_madina_arrival_campaign_segments_report,
          permissions.view_export_madina_group_details_report,
        ]}
        mode="any"
      >
        <CampaignAndGroupReports />
      </PermissionGate>
    ),
  },
  {
    path: "madina/campaigns-and-groups-reports/pilgrim-mission-arriving-leaving-statistics-report",
    Component: (
      <PermissionGate permissions={[permissions.view_export_madina_housed_arrivals_service_center_distribution_report]}>
        <PilgrimMissionArrivingLeavingStatisticsReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/campaigns-and-groups-reports/campaigns-and-groups-statement-report",
    Component: (
      <PermissionGate
        permissions={[
          permissions.view_export_madina_campaign_statement_report,
          permissions.view_export_madina_group_statement_report,
        ]}
        mode="any"
      >
        <UpcomingGroupsAndCampaignsDetailsReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/reception-and-arrival-reports",
    Component: <MadinaReceptionAndArrivalReports />,
  },
  {
    path: "madina/departure-reports",
    Component: (
      <PermissionGate
        permissions={[
          permissions.view_export_departure_request_report_madina,
          permissions.view_export_departure_campaign_report_madina,
        ]}
      >
        <MadinaDepartureReports />
      </PermissionGate>
    ),
  },
  {
    path: "madina/departure-reports/departure-request",
    Component: (
      <PermissionGate permissions={[permissions.view_export_departure_request_report_madina]}>
        <DepartureRequestReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/departure-reports/departure-campaigns",
    Component: (
      <PermissionGate permissions={[permissions.view_export_departure_campaign_report_madina]}>
        <DepartureCampaignsReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/departure-reports/departure-groups",
    Component: (
      <PermissionGate permissions={[permissions.view_export_departure_group_report_madina]}>
        <DepartureGroupsReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-reports",
    Component: <MadinaHousingReports />,
  },
  {
    path: "madina/housing-reports/housing-contract-detail",
    Component: (
      <PermissionGate permissions={[permissions.view_export_housing_contract_detail_report_for_madina]}>
        <HousingContractDetailReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-reports/house-contract-campaigns",
    Component: (
      <PermissionGate permissions={[permissions.view_export_house_contract_campaigns_report_for_madina]}>
        <HouseContractCampaignsReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-reports/food-contracts",
    Component: (
      <PermissionGate permissions={[permissions.view_export_food_contracts_report_for_madina]}>
        <FoodContractsReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-reports/housing-comprehensive-detail",
    Component: (
      <PermissionGate permissions={[permissions.view_export_housing_comprehensive_detail_report_for_madina]}>
        <HousingComprehensiveDetailReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-reports/housing-building-delegate-detail",
    Component: (
      <PermissionGate permissions={[permissions.view_export_housing_building_delegate_detail_report_for_madina]}>
        <HousingBuildingDelegateDetailReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/housing-reports/housing-inspection-orders-reports",
    Component: (
      <PermissionGate permissions={[permissions.view_export_madina_house_examination_report]}>
        <HousingInspectionOrdersReport />
      </PermissionGate>
    ),
  },
  {
    path: "madina/incident-and-report-logs",
    Component: (
      // <PermissionGate permissions={[permissions.view_export_madina_incident_reports_report]}>
      //   <MadinaIncidentAndReportLogs />
      // </PermissionGate>
      <PermissionGate permissions={[permissions.madina_view_export_incident_reports]}>
        <IncidentAndReportLogs />
      </PermissionGate>
    ),
  },
  {
    path: "madina/compliance-reports",
    Component: <MadinaComplianceReports />,
  },
  {
    path: "madina/users-and-service-centers",
    Component: <MadinaUsersAndServiceCenters />,
  },
  // System Management Routes
  {
    path: "madina/system-security",
    Component: <ActivityLogs />,
  },
];

export const routes = {
  makkah: makkahRoutes,
  user_management: usersManagementRoutes,
  seasonal_hr: seasonalRecruitmentRoutes,
  madina: madinaRoutes,
};
